import { DateAxis, LineSeries, ValueAxis, XYChart, CategoryAxis } from "@amcharts/amcharts4/charts";
import { color, create, Label, DropShadowFilter, Circle } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import "../../../assets/styles/component/legend.scss";
import { SOV_MIX_COLORS } from "../../../constants";
import { disposeChart } from "../helpers/chartHelpers";
import { configureDateAxis, configureValueAxis, lineSeriesConfiguration, configureCategoryAxis } from "../helpers/configurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import {
  dateAxisLabelFormatter_MonthlyFY,
  dateAxisLabelFormatter_QuarterlyFY,
  dateAxisLabelFormatter_YearlyFY,
  dateChinaAxisLabelFormatter_QuarterlyFY,
  dateChinaAxisLabelFormatter_YearlyFY,
  formatChinaDateAxisLabelToQuarterlyFY,
  formatChinaDateAxisLabelToYearlyFY,
  formatDateAxisLabelToQuarterlyFY,
  formatDateAxisLabelToYearlyFY,
} from "../helpers/pmoConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: any;
  granularity: string;
  theme: ThemeInt;
  chartId: string;
  seriesList: { name: string; field: string; hide: boolean; colour?: string }[];
  percentageChart?: boolean;
  nissanCompetitor?: string;
  market: string;
  diff: number;
  legendPosition?: "right" | "left" | "top" | "bottom";
  showSOVchartText?: boolean;
  nimLatestRanking: number;
}

class NIMLatestChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chartId = this.props.chartId;
  chart!: XYChart;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId);
      this.initChart();
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  initChart() {
    const {
      data,
      granularity,
      theme,
      seriesList,
      percentageChart,
      nissanCompetitor,
      market,
      diff,
      legendPosition = "top",
      showSOVchartText = false,
      nimLatestRanking,
    } = this.props;

    // Global chart configuration
    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.numberFormatter.numberFormat = "#a";
    this.chart.dateFormatter.inputDateFormat = "yyyy-MM-dd";
    this.chart.data = data;
    // Creates Legend
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.colors.list = SOV_MIX_COLORS.map((chartColor) => color(chartColor));
    this.chart.legend.marginBottom = 25;

    this.chart.legend.maxHeight = 100;
    this.chart.legend.scrollable = true;
    this.chart.legend.position = legendPosition;
    this.chart.maskBullets = false;

    // Create category axis
    const categoryAxis = this.chart.xAxes.push(new CategoryAxis());
    configureCategoryAxis(categoryAxis, theme);

    categoryAxis.dataFields.category = "market";
    // categoryAxis.title.text = "Country";
    categoryAxis.renderer.cellStartLocation = 0.0;
    categoryAxis.renderer.cellEndLocation = 0.0;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.minGridDistance = 10;
    categoryAxis.renderer.labels.template.maxWidth = 100;
    categoryAxis.renderer.labels.template.wrap = true;
    categoryAxis.renderer.labels.template.fontSize = 15;

    // categoryAxis.renderer.minGridDistance = 45;
    // add logic to display this for more than one year monthly
    if ((diff > 360 && granularity == "monthly") || diff > 720) {
      categoryAxis.renderer.labels.template.rotation = -90;
      categoryAxis.renderer.labels.template.verticalCenter = "middle";
      categoryAxis.renderer.labels.template.horizontalCenter = "left";
    }

    // Creates value axis
    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);

    // valueAxis.min = 0;
    valueAxis.max = nimLatestRanking;

    valueAxis.numberFormatter.numberFormat = "#.#a";
    valueAxis.renderer.inversed = true;
    valueAxis.renderer.minGridDistance = 1;
    valueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return text + `${percentageChart ? "%" : ""}`;
    });

    seriesList?.forEach(({ name, field, hide, colour }, idx) => {
      const series = this.chart.series.push(new LineSeries());
      series.connect = false;
      const bullet = lineSeriesConfiguration(series, {
        valueY: `${field}`,
        name: `${name}`,
      });
      nissanCompetitor == "all_competitors" ? (series.hidden = hide) : (series.hidden = false);
      series.dataFields.categoryX = "market";
      series.dataFields.valueY = field;
      //   series.dataFields.dateX = name;
      series.strokeWidth = 0;
      bullet.circle.radius = 5;
      if (colour) {
        series.fill = color(colour);
        series.stroke = color(colour);
      }

      //   if nissane put color red
      if (name == "Nissan") {
        series.fill = color("#FF0000");
        bullet.circle.stroke = color("#FFCCCC"); // Lighter red stroke
        bullet.circle.radius = 9;
        const shadow = new DropShadowFilter();
        shadow.opacity = 0.2; // Lighter shadow
        shadow.blur = 5;
        bullet.circle.filters.push(shadow); // Add shadow

        const innerCircle = bullet.createChild(Circle);
        innerCircle.radius = 7;
        innerCircle.fill = color("#FF0000");
        innerCircle.stroke = color("#FFFFFF"); // White stroke
        innerCircle.strokeWidth = 2;
      }
      bullet.adapter.add("tooltipText", (text, target) => {
        const dataItem = target?.dataItem?.dataContext as Record<string, any>;
        const date = target?.dataItem?.dates?.dateX;
        const score = dataItem ? dataItem[`${field}_score`] : null;

        if (granularity === "quarterly") {
          //@ts-ignore
          const dateVal = market == "China" || market == "China_Ev" ? formatChinaDateAxisLabelToQuarterlyFY(date) : formatDateAxisLabelToQuarterlyFY(date);
          text = `[bold; font-size: var(--regular_font_size);] Brand: ${field} \n[bold; font-size: var(--regular_font_size);] Rank: {valueY.formatNumber("#,###.0")} \n[bold; font-size: var(--regular_font_size);] Score: ${score}%`;
        } else if (granularity === "yearly") {
          //@ts-ignore
          const dateVal = market == "China" || market == "China_Ev" ? formatChinaDateAxisLabelToYearlyFY(date) : formatDateAxisLabelToYearlyFY(date);
          text = `[bold; font-size: var(--regular_font_size);] Brand: ${field} \n[bold; font-size: var(--regular_font_size);] Rank: {valueY.formatNumber("#,###.0")} \n[bold; font-size: var(--regular_font_size);] Score: ${score}%`;
        } else {
          text = `[bold; font-size: var(--regular_font_size);] Brand: ${field} \n[bold; font-size: var(--regular_font_size);] Rank: {valueY.formatNumber("#,###.0")} \n[bold; font-size: var(--regular_font_size);] Score: ${score}%`;
        }
        return text;
      });
    });
  }
  render() {
    return (
      <div className={"chartWrapper"}>
        <div id={this.props.chartId} data-test-id={this.props.chartId} className={"graph"} />
      </div>
    );
  }
}

export default NIMLatestChart;
